import { useState } from "react";
import {
  generateAadhaarOtpApi,
  verifyAadhaarApi,
  startDigilockerVerificationApi,
} from "../../api/verification";
import { VerificationStatus } from "./VerificationStatus";
import { OtpVerification } from "./OtpVerification";
import { C, font } from "../../styles/tokens";

export function AadhaarVerification({
  aadhaarNumber = "",
  onAadhaarChange,
  onVerificationSuccess,
}) {
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [step, setStep] = useState("INPUT"); // "INPUT" | "OTP" | "REDIRECTING" | "VERIFIED"
  const [sessionId, setSessionId] = useState("");
  const [verifiedData, setVerifiedData] = useState(null);

  const cleanAadhaar = aadhaarNumber?.replace(/\D/g, "") || "";
  const isValidAadhaar = /^[2-9][0-9]{11}$/.test(cleanAadhaar);

  const handleSendOtp = async () => {
    if (!consent || !isValidAadhaar || loading) return;
    setError(null);
    setLoading(true);

    try {
      const data = await generateAadhaarOtpApi({
        aadhaarNumber: cleanAadhaar,
        consent,
      });

      const activeSessionId =
        data?.ref_id ||
        data?.sessionId ||
        data?.refId ||
        data?.data?.ref_id ||
        data?.data?.sessionId;
      if (activeSessionId) {
        setSessionId(activeSessionId);
        setStep("OTP");
      } else {
        throw new Error(
          "Did not receive session reference ID from verification service.",
        );
      }
    } catch (err) {
      setError(
        err?.message ||
          "Failed to send Aadhaar OTP. Please check your Aadhaar number.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    if (!otp || loading) return;
    setError(null);
    setLoading(true);

    try {
      const data = await verifyAadhaarApi({
        sessionId,
        otp,
      });

      if (data?.verificationToken) {
        setVerifiedData(data);
        setStep("VERIFIED");
        if (onVerificationSuccess) {
          const rawPhone = data.phone || data.mobile || undefined;
          const phone = rawPhone === "9876543210" ? undefined : rawPhone;
          onVerificationSuccess({
            verificationToken: data.verificationToken,
            name: data.name,
            maskedAadhaar: data.maskedAadhaar,
            phone,
            mobile: phone,
            dob: data.dob || data.dateOfBirth || undefined,
          });
        }
      } else {
        throw new Error("Verification completed but no token was returned.");
      }
    } catch (err) {
      setError(err?.message || "OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartDigilocker = async () => {
    if (loading) return;
    setError(null);
    setLoading(true);

    try {
      const data = await startDigilockerVerificationApi({
        aadhaarNumber: cleanAadhaar || undefined,
        consent: true,
        redirectUrl: `${window.location.origin}/verification/callback`,
      });

      const targetUrl = data?.redirectUrl || data?.data?.redirectUrl;

      if (targetUrl) {
        setStep("REDIRECTING");
        window.location.href = targetUrl;
      } else {
        throw new Error("Did not receive DigiLocker redirect link.");
      }
    } catch (err) {
      setError(err?.message || "Failed to initiate DigiLocker verification.");
      setLoading(false);
    }
  };

  return (
    <div style={boxStyle}>
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <svg
            style={{ width: 18, height: 18, color: C.blue }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <span style={{ fontWeight: 600, fontSize: 13, color: C.textDark }}>
            Aadhaar Identity Verification (Professional)
          </span>
        </div>
        <span style={badgeStyle}>Cashfree KYC</span>
      </div>

      {step === "INPUT" && (
        <div style={{ marginTop: 10 }}>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <input
              type="text"
              placeholder="12-digit Aadhaar Number"
              value={aadhaarNumber}
              onChange={(e) =>
                onAadhaarChange &&
                onAadhaarChange(e.target.value.replace(/\D/g, ""))
              }
              disabled={loading}
              maxLength={12}
              className="w-full sm:flex-1 px-3 py-2 text-base sm:text-sm border border-slate-300 rounded-md tracking-widest outline-none focus:border-blue-500"
              style={{ fontFamily: font }}
            />

            <button
              type="button"
              onClick={handleSendOtp}
              disabled={!consent || !isValidAadhaar || loading}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm sm:text-[13px] font-semibold text-white rounded-md transition duration-150 flex items-center justify-center shrink-0"
              style={{
                background:
                  consent && isValidAadhaar && !loading ? C.blue : C.textLight,
                cursor:
                  consent && isValidAadhaar && !loading ? "pointer" : "default",
                fontFamily: font,
              }}
            >
              {loading ? "Sending OTP..." : "Get OTP"}
            </button>
          </div>

          <label
            className="flex items-start gap-2.5 mt-3 text-xs cursor-pointer select-none py-1"
            style={{
              color: C.textMed,
              opacity: isValidAadhaar ? 1 : 0.6,
              cursor: isValidAadhaar ? "pointer" : "not-allowed",
              fontFamily: font,
            }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={!isValidAadhaar || loading}
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ accentColor: C.blue }}
            />
            <span className="leading-snug">
              I consent to authenticate my identity via Cashfree Aadhaar eKYC
              service.
            </span>
          </label>

          <div style={{ marginTop: 10, textAlign: "right" }}>
            <button
              type="button"
              onClick={handleStartDigilocker}
              disabled={loading}
              style={{
                background: "none",
                border: 0,
                color: C.blue,
                fontSize: 11,
                cursor: consent ? "default" : "default",
                textDecoration: "underline",
              }}
            >
              Or verify with DigiLocker
            </button>
          </div>

          {error && <div style={errorBoxStyle}>{error}</div>}
        </div>
      )}

      {step === "OTP" && (
        <OtpVerification
          onVerify={handleVerifyOtp}
          onResend={handleSendOtp}
          loading={loading}
          error={error}
        />
      )}

      {step === "REDIRECTING" && (
        <div style={{ textAlign: "center", padding: "16px 8px" }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: C.blue,
              marginBottom: 4,
            }}
          >
            Redirecting to DigiLocker Portal...
          </div>
          <div style={{ fontSize: 11, color: C.textMed }}>
            Please grant consent on the Cashfree / DigiLocker window.
          </div>
        </div>
      )}

      {step === "VERIFIED" && verifiedData && (
        <VerificationStatus
          status="VERIFIED"
          type="AADHAAR"
          verifiedAt={new Date().toISOString()}
          details={{
            maskedAadhaar: verifiedData.maskedAadhaar,
            name: verifiedData.name,
          }}
        />
      )}
    </div>
  );
}

const boxStyle = {
  border: `1px solid ${C.divider || "#cbd5e1"}`,
  borderRadius: 8,
  padding: 14,
  margin: "10px 0 14px",
  background: "#ffffff",
  fontFamily: font,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const badgeStyle = {
  fontSize: 10,
  fontWeight: 700,
  background: "#E7F4EC",
  color: "#08833E",
  padding: "3px 8px",
  borderRadius: 4,
  border: "1px solid rgba(8, 131, 62, 0.2)",
};

const inputStyle = {
  flex: 1,
  padding: "8px 12px",
  fontSize: 13,
  letterSpacing: 2,
  border: `1px solid ${C.dividerStrong || "#cbd5e1"}`,
  borderRadius: 6,
  fontFamily: font,
};

const buttonStyle = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  color: "white",
  border: 0,
  borderRadius: 6,
  fontFamily: font,
};

const consentLabelStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  fontSize: 11,
  color: C.textMed,
  marginTop: 10,
  cursor: "pointer",
};

const errorBoxStyle = {
  fontSize: 11,
  color: C.red || "#ef4444",
  background: C.redLight || "#fef2f2",
  padding: "6px 10px",
  borderRadius: 4,
  marginTop: 8,
};
